import { Group, Rect, util as fabricUtil } from 'fabric';
import { applyLayerOrder } from '../../../constants/layerDefinitions';

// Priority order for layer inheritance: higher number = higher priority.
// A group inherits the layer of its highest-priority sub-object.
const LAYER_PRIORITY = {
  background: 1,
  outline: 2,
  structures: 3,
  beds: 4,
  misc: 5,
  plants: 6,
};

function getHighestLayer(objects) {
  return objects.reduce((highest, obj) => {
    const objLayer = obj.data?.layer || 'misc';
    return (LAYER_PRIORITY[objLayer] || 0) > (LAYER_PRIORITY[highest] || 0)
      ? objLayer
      : highest;
  }, objects[0]?.data?.layer || 'misc');
}

/**
 * Creates a fabric.Group from an array of canvas objects.
 * Objects must already be on the canvas.
 * Saves original layer info on each object for later restoration on ungroup.
 *
 * @param {fabric.Canvas} canvas
 * @param {fabric.Object[]} objects - At least 2 objects
 * @returns {{ group: fabric.Group } | null}
 */
export function groupObjects(canvas, objects) {
  if (!canvas || !objects || objects.length < 2) return null;

  // Save original layer info before grouping
  objects.forEach(obj => {
    if (!obj.data) obj.data = {};
    obj.data.originalLayer = obj.data.layer;
    obj.data.originalZIndex = obj.data.zIndexWithinLayer;
  });

  // Group inherits highest-priority layer among sub-objects
  const groupLayer = getHighestLayer(objects);

  // Compute max zIndex so the group sits on top within its layer
  const allNonGrid = canvas.getObjects().filter(
    o => !o.data?.isGridLine && !o.data?.isSnapGuide
  );
  const maxZ = allNonGrid.reduce(
    (max, o) => Math.max(max, o.data?.zIndexWithinLayer ?? 0),
    -1
  );

  // Remove individual objects from canvas before creating the group.
  // Fabric.js Group constructor adjusts child coordinates to be group-relative
  // via LayoutManager initialization.
  objects.forEach(obj => canvas.remove(obj));

  const group = new Group(objects, {
    originX: 'left',
    originY: 'top',
    data: {
      type: 'group',
      layer: groupLayer,
      zIndexWithinLayer: maxZ + 1,
      originalObjectLayers: objects.map(obj => ({
        layer: obj.data?.originalLayer,
        zIndexWithinLayer: obj.data?.originalZIndex,
      })),
    },
    objectType: 'group',
  });

  canvas.add(group);
  applyLayerOrder(canvas);
  canvas.setActiveObject(group);
  canvas.renderAll();

  return { group };
}

/**
 * Dissolves a fabric.Group back into individual canvas objects.
 * Restores original layer assignments.
 *
 * Uses Fabric.js v6 native group.remove(obj) which calls _exitGroup():
 * - Clears obj.group reference first
 * - Then applies the group transform via applyTransformToObject() so the
 *   object ends up at the correct canvas-absolute position.
 *
 * @param {fabric.Canvas} canvas
 * @param {fabric.Group} group
 * @returns {{ objects: fabric.Object[] } | null}
 */
export function ungroupObject(canvas, group) {
  if (!canvas || !group) return null;

  const subObjects = [...group.getObjects()]; // snapshot before any mutation
  const originalLayers = group.data?.originalObjectLayers || [];

  // Snapshot the canvas-absolute transform of each sub-object BEFORE any
  // mutations. While obj.group is still set, calcTransformMatrix() returns
  // the full absolute canvas matrix (= group.calcTransformMatrix() × obj.calcOwnMatrix()).
  // All snapshots must happen before canvas.remove(group) so the group's
  // own transform is still valid.
  const absoluteTransforms = subObjects.map(obj => obj.calcTransformMatrix());

  // Remove the group from canvas
  canvas.remove(group);

  // Apply pre-snapshotted absolute transforms and re-add as standalone objects
  subObjects.forEach((obj, i) => {
    obj.group = undefined;
    obj.parent = undefined;
    fabricUtil.applyTransformToObject(obj, absoluteTransforms[i]);
    obj.setCoords();

    if (!obj.data) obj.data = {};
    if (originalLayers[i]) {
      obj.data.layer = originalLayers[i].layer;
      obj.data.zIndexWithinLayer = originalLayers[i].zIndexWithinLayer;
    }
    canvas.add(obj);
  });

  applyLayerOrder(canvas);
  canvas.discardActiveObject();
  canvas.renderAll();

  return { objects: subObjects };
}

/**
 * Returns the indicator rect properties matching the group's current
 * position, size, rotation and scale in canvas coordinates.
 */
function _indicatorProps(group) {
  return {
    left: group.left,
    top: group.top,
    width: group.width,
    height: group.height,
    angle: group.angle || 0,
    scaleX: group.scaleX || 1,
    scaleY: group.scaleY || 1,
    skewX: group.skewX || 0,
    skewY: group.skewY || 0,
  };
}

/**
 * Activates interactive edit mode for a group.
 *
 * Adds a non-interactive Rect overlay with a dashed emerald border
 * as the visual indicator. This avoids 'after:render' hooks (which
 * cause performance degradation) and the limitation that Group.drawObject()
 * never renders its own stroke.
 *
 * @param {fabric.Canvas} canvas
 * @param {fabric.Group} group
 */
export function enterEditMode(canvas, group) {
  group.set({
    interactive: true,
    subTargetCheck: true,
    hasControls: false, // hide standard scale/rotate handles
  });

  // Overlay Rect: same position, size, rotation as the group
  const indicator = new Rect({
    ..._indicatorProps(group),
    stroke: '#10B981',
    strokeWidth: 2,
    strokeDashArray: [6, 4],
    fill: 'transparent',
    selectable: false,
    evented: false,
    hasBorders: false,
    hasControls: false,
    originX: 'left',
    originY: 'top',
    data: { isEditModeIndicator: true },
  });

  canvas.add(indicator);
  indicator.setCoords();
  group._editIndicator = indicator;

  // Keep indicator in sync when the group is transformed
  const syncIndicator = (e) => {
    if (e?.target !== group) return;
    indicator.set(_indicatorProps(group));
    indicator.setCoords();
  };
  group._editIndicatorSync = syncIndicator;
  canvas.on('object:moving', syncIndicator);
  canvas.on('object:scaling', syncIndicator);
  canvas.on('object:rotating', syncIndicator);
  canvas.on('object:modified', syncIndicator);

  canvas.setActiveObject(group);
  canvas.renderAll();
}

/**
 * Exits interactive edit mode for a group.
 * Removes the indicator overlay and restores standard controls.
 *
 * @param {fabric.Canvas} canvas
 * @param {fabric.Group} group
 */
export function exitEditMode(canvas, group) {
  group.set({
    interactive: false,
    subTargetCheck: false,
    hasControls: true,
  });

  if (group._editIndicator) {
    canvas.remove(group._editIndicator);
    delete group._editIndicator;
  }

  if (group._editIndicatorSync) {
    canvas.off('object:moving', group._editIndicatorSync);
    canvas.off('object:scaling', group._editIndicatorSync);
    canvas.off('object:rotating', group._editIndicatorSync);
    canvas.off('object:modified', group._editIndicatorSync);
    delete group._editIndicatorSync;
  }

  canvas.discardActiveObject();
  canvas.renderAll();
}

/**
 * Returns true if obj is a user-created group (not a PlantCircle).
 *
 * PlantCircle extends fabric.Group, so its type is also 'group'.
 * We distinguish by objectType: PlantCircle has objectType === 'plant'.
 *
 * @param {fabric.Object|null|undefined} obj
 * @returns {boolean}
 */
export function isUserGroup(obj) {
  return obj?.type === 'group' && obj?.objectType !== 'plant';
}
